/* Modular builder — /pages/modular configurator controller.
   Range switching, fabric selection, quantity steppers, a popular-setup
   preset, a free-piece progress bar, a live build summary and a mobile
   sticky bar. Cart / add-to-cart is deferred — the ATC buttons are
   placeholders. See design_handoff_homepage/README.md PART 2. */

function formatMoney(cents, format) {
  const value = (cents || 0) / 100;
  const fmt = format || '${{amount}}';

  function withCommas(num, decimals) {
    const fixed = num.toFixed(decimals);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, token) {
    switch (token) {
      case 'amount':
        return withCommas(value, 2);
      case 'amount_no_decimals':
        return withCommas(value, 0);
      case 'amount_with_comma_separator':
        return withCommas(value, 2).replace(/,/g, ' ').replace('.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withCommas(value, 0).replace(/,/g, '.');
      case 'amount_with_space_separator':
        return withCommas(value, 2).replace(/,/g, ' ');
      case 'amount_no_decimals_with_space_separator':
        return withCommas(value, 0).replace(/,/g, ' ');
      case 'amount_with_apostrophe_separator':
        return withCommas(value, 2).replace(/,/g, "'");
      default:
        return withCommas(value, 2);
    }
  });
}

class ModularBuilder extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.ranges = Array.from(this.querySelectorAll('[data-mb-range]'));
    this.pills = Array.from(this.querySelectorAll('[data-mb-range-pill]'));
    this.crumb = this.querySelector('[data-mb-crumb]');
    this.otherCards = Array.from(this.querySelectorAll('[data-mb-other]'));
    this.bar = this.querySelector('[data-mb-bar]');
    this.selectedFabric = null;

    if (!this.ranges.length) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => this.switchRange(pill.dataset.mbRangePill, true));
    });

    this.addEventListener('click', (event) => this.onClick(event));

    const initial = this.rangeFromUrl() || this.ranges[0].dataset.mbRange;
    this.switchRange(initial, false);
  }

  rangeFromUrl() {
    const param = new URLSearchParams(window.location.search).get('range');
    if (!param) return null;
    const match = this.ranges.find((r) => r.dataset.mbRange === param);
    return match ? param : null;
  }

  rangeEl(slug) {
    return this.ranges.find((r) => r.dataset.mbRange === slug) || null;
  }

  /* The piece flagged as the free bonus (e.g. the ottoman), or null. */
  bonusPiece(range) {
    return range.querySelector('[data-bonus-piece]');
  }

  onClick(event) {
    const fabric = event.target.closest('[data-mb-fabric]');
    if (fabric && this.contains(fabric)) {
      this.selectFabric(fabric.dataset.mbFabric);
      return;
    }
    const preset = event.target.closest('[data-mb-preset-apply]');
    if (preset) {
      const range = event.target.closest('[data-mb-range]');
      if (range) this.applyPreset(range);
      return;
    }
    const inc = event.target.closest('[data-mb-inc]');
    const dec = event.target.closest('[data-mb-dec]');
    if (inc || dec) {
      const piece = event.target.closest('[data-mb-piece]');
      if (piece) this.changeQty(piece, inc ? 1 : -1);
      return;
    }
    const atc = event.target.closest('[data-mb-atc], [data-mb-bar-atc]');
    if (atc && !atc.disabled) {
      const range = this.rangeEl(this.activeRange);
      if (range) this.addToCart(range);
    }
  }

  /* Batch-adds the selected pieces to the cart. When the range's free-piece
     threshold is met, the bonus product (e.g. the ottoman) is added too —
     the Shopify "Buy X get Y" automatic discount then prices it free. */
  addToCart(range) {
    if (this.adding) return;

    if (!this.fabricSatisfied(range)) {
      this.showError(range, 'Please choose a fabric for your build first.');
      return;
    }

    /* The chosen fabric rides along on every line as a cart property so it
       reaches the cart and order — pieces have no per-fabric variant. */
    const fabricProps = this.selectedFabric ? { Fabric: this.selectedFabric } : null;

    const items = [];
    let count = 0;
    range.querySelectorAll('[data-mb-piece]').forEach((piece) => {
      const qty = parseInt(piece.querySelector('[data-mb-qty]').textContent, 10) || 0;
      const variantId = piece.dataset.variantId;
      if (qty > 0 && variantId) {
        const item = { id: Number(variantId), quantity: qty };
        if (fabricProps) item.properties = Object.assign({}, fabricProps);
        items.push(item);
        count += qty;
      }
    });
    if (!items.length) return;

    const bonus = this.bonusPiece(range);
    if (bonus && bonus.dataset.variantId) {
      const threshold = parseInt(range.dataset.bonusThreshold, 10) || 3;
      if (count >= threshold) {
        items.push({
          id: Number(bonus.dataset.variantId),
          quantity: 1,
          properties: Object.assign(
            { _promo_offer: 'modular_free_bonus', _promo_auto_added: 'true' },
            fabricProps || {}
          )
        });
      }
    }

    const notice = range.querySelector('[data-mb-notice]');
    if (notice) {
      notice.hidden = true;
      notice.textContent = '';
    }

    this.adding = true;
    this.setAtcLoading(range, true);

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data: data })))
      .then((result) => {
        if (!result.ok) {
          throw new Error((result.data && result.data.description) || 'Could not add your build to the cart.');
        }
        if (window.eventBus) {
          window.eventBus.emit('render:cart:drawer');
          window.eventBus.emit('open:cart:drawer', { scrollToTop: true });
        } else {
          window.location.href = root + 'cart';
        }
      })
      .catch((error) => {
        this.showError(range, error && error.message);
      })
      .finally(() => {
        this.adding = false;
        this.setAtcLoading(range, false);
        this.renderSummary(range);
      });
  }

  setAtcLoading(range, loading) {
    const summaryAtc = range.querySelector('[data-mb-atc]');
    if (summaryAtc) {
      summaryAtc.disabled = loading;
      if (loading) summaryAtc.textContent = 'Adding…';
    }
    if (this.bar) {
      const barAtc = this.bar.querySelector('[data-mb-bar-atc]');
      if (barAtc) {
        barAtc.disabled = loading;
        barAtc.textContent = loading ? 'Adding…' : 'Add all to cart';
      }
    }
  }

  showError(range, message) {
    const notice = range.querySelector('[data-mb-notice]');
    if (!notice) return;
    notice.textContent = message || 'Something went wrong. Please try again.';
    notice.hidden = false;
  }

  switchRange(slug, fromInteraction) {
    const target = this.rangeEl(slug);
    if (!target) return;
    this.activeRange = slug;

    this.ranges.forEach((range) => {
      const isActive = range === target;
      range.hidden = !isActive;
      if (fromInteraction && isActive) this.resetRange(range);
    });

    this.pills.forEach((pill) => {
      const isActive = pill.dataset.mbRangePill === slug;
      pill.classList.toggle('mb-pill--active', isActive);
      pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.otherCards.forEach((card) => {
      card.hidden = card.dataset.mbOther === slug;
    });

    if (this.crumb) this.crumb.textContent = (target.dataset.rangeName || 'Modular') + ' Modular';

    this.applyFabric(target);
    this.renderSummary(target);

    if (fromInteraction) {
      const url = new URL(window.location.href);
      url.searchParams.set('range', slug);
      window.history.replaceState({}, '', url);
      this.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
  }

  resetRange(range) {
    range.querySelectorAll('[data-mb-piece]').forEach((piece) => {
      const qty = piece.querySelector('[data-mb-qty]');
      if (qty) qty.textContent = '0';
      piece.classList.remove('mb-piece--active');
    });
  }

  selectFabric(name) {
    this.selectedFabric = name;
    this.ranges.forEach((range) => this.applyFabric(range));
  }

  applyFabric(range) {
    range.querySelectorAll('[data-mb-fabric]').forEach((btn) => {
      const isActive = btn.dataset.mbFabric === this.selectedFabric;
      btn.classList.toggle('mb-fabric--selected', isActive);
      btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
    });
    this.updateLockState(range);
    this.renderSummary(range);
  }

  /* A range that offers fabric swatches requires one to be picked before
     the build step unlocks. Ranges with no swatches configured are exempt. */
  fabricRequired(range) {
    return !!range.querySelector('[data-mb-fabric]');
  }

  fabricSatisfied(range) {
    return !this.fabricRequired(range) || !!this.selectedFabric;
  }

  /* Locks "Step 02 — Build your layout" until a fabric is chosen, so the
     UI walks the shopper through fabric first, then pieces. */
  updateLockState(range) {
    const buildStep = range.querySelector('[data-mb-build-step]');
    if (!buildStep) return;
    const locked = !this.fabricSatisfied(range);
    buildStep.classList.toggle('mb-step--locked', locked);
    buildStep.setAttribute('aria-disabled', locked ? 'true' : 'false');
  }

  changeQty(piece, delta) {
    const qtyEl = piece.querySelector('[data-mb-qty]');
    if (!qtyEl) return;
    const next = Math.max(0, Math.min(99, (parseInt(qtyEl.textContent, 10) || 0) + delta));
    qtyEl.textContent = String(next);
    piece.classList.toggle('mb-piece--active', next > 0);
    const range = piece.closest('[data-mb-range]');
    if (range) this.renderSummary(range);
  }

  /* "Apply popular setup" — uses each piece's popular quantity, or falls
     back to 1 on the first `threshold` pieces when none are configured. */
  applyPreset(range) {
    const pieces = Array.from(range.querySelectorAll('[data-mb-piece]'));
    const anyPopular = pieces.some((p) => (parseInt(p.dataset.popular, 10) || 0) > 0);
    const fallback = parseInt(range.dataset.bonusThreshold, 10) || 3;

    pieces.forEach((piece, index) => {
      let qty = parseInt(piece.dataset.popular, 10) || 0;
      if (!anyPopular) qty = index < fallback ? 1 : 0;
      const qtyEl = piece.querySelector('[data-mb-qty]');
      if (qtyEl) qtyEl.textContent = String(qty);
      piece.classList.toggle('mb-piece--active', qty > 0);
    });

    this.renderSummary(range);
  }

  computeRange(range) {
    let count = 0;
    let total = 0;
    const items = [];
    range.querySelectorAll('[data-mb-piece]').forEach((piece) => {
      const qty = parseInt(piece.querySelector('[data-mb-qty]').textContent, 10) || 0;
      if (qty <= 0) return;
      const price = parseInt(piece.dataset.price, 10) || 0;
      count += qty;
      total += price * qty;
      items.push({ label: piece.dataset.pieceLabel || 'Piece', qty: qty, lineTotal: price * qty });
    });
    return { count: count, total: total, items: items };
  }

  renderSummary(range) {
    const linesEl = range.querySelector('[data-mb-summary-lines]');
    if (!linesEl) return;

    const result = this.computeRange(range);
    const hasItems = result.count > 0;

    linesEl.innerHTML = '';
    result.items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'mb-summary__line';
      const name = document.createElement('span');
      name.className = 'mb-summary__line-name';
      name.textContent = item.qty + ' × ' + item.label;
      const amount = document.createElement('span');
      amount.className = 'mb-summary__line-price';
      amount.textContent = formatMoney(item.lineTotal, this.moneyFormat);
      li.append(name, amount);
      linesEl.appendChild(li);
    });

    const emptyEl = range.querySelector('[data-mb-summary-empty]');
    const totalsEl = range.querySelector('[data-mb-summary-totals]');
    const totalEl = range.querySelector('[data-mb-summary-total]');
    if (emptyEl) emptyEl.hidden = hasItems;
    if (totalsEl) totalsEl.hidden = !hasItems;
    if (totalEl) totalEl.textContent = formatMoney(result.total, this.moneyFormat);

    const bonusEl = range.querySelector('[data-mb-summary-bonus]');
    if (bonusEl) {
      const bonus = this.bonusPiece(range);
      const bonusThreshold = parseInt(range.dataset.bonusThreshold, 10) || 3;
      const qualifies = !!bonus && result.count >= bonusThreshold;
      bonusEl.hidden = !qualifies;
      if (qualifies) {
        const nameEl = bonusEl.querySelector('[data-mb-bonus-name]');
        if (nameEl) nameEl.textContent = bonus.dataset.pieceLabel || 'Bonus piece';
      }
    }

    const fabricEl = range.querySelector('[data-mb-summary-fabric]');
    if (fabricEl) {
      if (this.selectedFabric) {
        fabricEl.hidden = false;
        fabricEl.textContent = 'Fabric — ' + this.selectedFabric;
      } else {
        fabricEl.hidden = true;
      }
    }

    const fabricOk = this.fabricSatisfied(range);
    const atc = range.querySelector('[data-mb-atc]');
    if (atc) {
      atc.disabled = !hasItems || !fabricOk;
      if (!fabricOk) {
        atc.textContent = 'Choose a fabric first';
      } else {
        atc.textContent = hasItems ? 'Add all to cart' : 'Add pieces to continue';
      }
      if (!hasItems) {
        const notice = range.querySelector('[data-mb-notice]');
        if (notice) notice.hidden = true;
      }
    }

    this.renderProgress(range, result.count);

    if (range.dataset.mbRange === this.activeRange) {
      this.syncBar(range, result.count, result.total);
    }
  }

  progressMessage(count, threshold, reward) {
    const remaining = threshold - count;
    if (remaining <= 0) return 'Free ' + reward + ' unlocked';
    return 'Add ' + remaining + ' more piece' + (remaining === 1 ? '' : 's') +
      ' to unlock your free ' + reward;
  }

  renderProgress(range, count) {
    const wrap = range.querySelector('[data-mb-progress]');
    if (!wrap) return;
    const bonus = this.bonusPiece(range);
    if (!bonus) {
      wrap.hidden = true;
      return;
    }
    wrap.hidden = false;
    const threshold = parseInt(range.dataset.bonusThreshold, 10) || 3;
    const reward = bonus.dataset.pieceLabel || 'bonus piece';
    const fill = range.querySelector('[data-mb-progress-fill]');
    if (fill) fill.style.width = Math.min(count / threshold, 1) * 100 + '%';
    const msg = range.querySelector('[data-mb-progress-msg]');
    if (msg) msg.textContent = this.progressMessage(count, threshold, reward);
    wrap.classList.toggle('mb-progress--complete', count >= threshold);
  }

  syncBar(range, count, total) {
    if (!this.bar) return;
    if (count === undefined) {
      const result = this.computeRange(range);
      count = result.count;
      total = result.total;
    }

    const countEl = this.bar.querySelector('[data-mb-bar-count]');
    if (countEl) countEl.textContent = count + (count === 1 ? ' piece' : ' pieces');
    const totalEl = this.bar.querySelector('[data-mb-bar-total]');
    if (totalEl) totalEl.textContent = count > 0 ? formatMoney(total, this.moneyFormat) : '';
    const atc = this.bar.querySelector('[data-mb-bar-atc]');
    if (atc) atc.disabled = count <= 0 || !this.fabricSatisfied(range);

    const progress = this.bar.querySelector('[data-mb-bar-progress]');
    if (progress) {
      if (this.bonusPiece(range)) {
        progress.hidden = false;
        const threshold = parseInt(range.dataset.bonusThreshold, 10) || 3;
        const fill = progress.querySelector('[data-mb-bar-progress-fill]');
        if (fill) fill.style.width = Math.min(count / threshold, 1) * 100 + '%';
      } else {
        progress.hidden = true;
      }
    }
  }
}

customElements.define('modular-builder', ModularBuilder);
